/**
 * JourneyReportButton - Export station reports for a journey
 *
 * Dropdown button that allows users to download station analysis
 * reports in HTML or PDF format. Only shows stations that have completed results.
 *
 * @version 1.0.0
 */

import React, { useState, useRef, useEffect } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import { useJourneyId } from '../../contexts/JourneyContext';
import './JourneyReportButton.css';

interface StationAvailability {
  station: string;
  label: string;
  available: boolean;
  formats: string[];
}

interface JourneyReportButtonProps {
  groupId?: string;
  disabled?: boolean;
}

export const JourneyReportButton: React.FC<JourneyReportButtonProps> = ({
  groupId: groupIdProp,
  disabled = false,
}) => {
  const groupId = useJourneyId(groupIdProp);
  const { authFetch } = useApiClient();
  const [isOpen, setIsOpen] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [availableStations, setAvailableStations] = useState<StationAvailability[]>([]);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load available stations when dropdown opens
  useEffect(() => {
    if (!isOpen || availableStations.length > 0) return;

    const loadAvailability = async () => {
      setLoadingAvailability(true);
      try {
        const response = await authFetch(`/api/journey/${groupId}/report/available`);
        if (response.ok) {
          const data = await response.json();
          setAvailableStations(data.available || []);
        }
      } catch (err) {
        console.error('[JourneyReportButton] Failed to load availability:', err);
      } finally {
        setLoadingAvailability(false);
      }
    };

    loadAvailability();
  }, [isOpen, groupId, authFetch, availableStations.length]);

  const handleDownload = async (station: string, format: string) => {
    const key = `${station}-${format}`;
    setDownloading(key);
    setError(null);
    setIsOpen(false);

    try {
      const response = await authFetch(
        `/api/journey/${groupId}/report?station=${station}&format=${format}`
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: { message: 'Download failed' } }));
        throw new Error(errorData.error?.message || `Failed to generate ${format.toUpperCase()} report`);
      }

      const contentDisposition = response.headers.get('Content-Disposition');
      const filenameMatch = contentDisposition?.match(/filename="?([^"]+)"?/);
      const filename = filenameMatch?.[1] || `Station_Report.${format}`;

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(`[JourneyReportButton] Download error:`, err);
      setError(err instanceof Error ? err.message : 'Failed to download report');
    } finally {
      setDownloading(null);
    }
  };

  const completedStations = availableStations.filter(s => s.available);

  return (
    <div className="journey-report-button" ref={dropdownRef}>
      <button
        className="journey-report-trigger"
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled || downloading !== null}
        title="Export station reports"
      >
        {downloading ? (
          <>
            <span className="journey-report-spinner" />
            Generating...
          </>
        ) : (
          <>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 1v9M4.5 6.5L8 10l3.5-3.5M2 12v2h12v-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Export Reports
            <svg className="journey-report-chevron" width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </>
        )}
      </button>

      {isOpen && (
        <div className="journey-report-dropdown">
          {loadingAvailability ? (
            <div className="journey-report-loading">
              <span className="journey-report-spinner" />
              Loading available reports...
            </div>
          ) : completedStations.length === 0 ? (
            <div className="journey-report-empty">
              No completed stations available for reporting.
            </div>
          ) : (
            completedStations.map(station => (
              <div key={station.station} className="journey-report-station-group">
                <div className="journey-report-station-label">{station.label}</div>
                <div className="journey-report-format-row">
                  {/* Render only the formats the server says this report
                      supports — the executive summary (DC-1644) is HTML-only
                      until its phase-2 PDF path lands. */}
                  {station.formats.includes('html') && (
                    <button
                      className="journey-report-format-btn"
                      onClick={() => handleDownload(station.station, 'html')}
                      disabled={downloading !== null}
                      title={`Download ${station.label} as HTML`}
                    >
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M2 2h12v12H2z" stroke="currentColor" strokeWidth="1.2"/>
                        <path d="M4 5h8M4 8h6M4 11h4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                      </svg>
                      HTML
                    </button>
                  )}
                  {station.formats.includes('pdf') && (
                    <button
                      className="journey-report-format-btn"
                      onClick={() => handleDownload(station.station, 'pdf')}
                      disabled={downloading !== null}
                      title={`Download ${station.label} as PDF`}
                    >
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 1h7l3 3v11H3V1z" stroke="currentColor" strokeWidth="1.2"/>
                        <path d="M10 1v3h3" stroke="currentColor" strokeWidth="1.2"/>
                        <text x="5" y="11" fontSize="5" fill="currentColor" fontWeight="bold">PDF</text>
                      </svg>
                      PDF
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {error && (
        <div className="journey-report-error">
          {error}
          <button className="journey-report-error-dismiss" onClick={() => setError(null)}>
            &times;
          </button>
        </div>
      )}
    </div>
  );
};

export default JourneyReportButton;
